import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdvancedWebScanner = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [viewMode, setViewMode] = useState('form');

  const [options, setOptions] = useState({
    headers: true,
    technologies: true,
    ssl: true,
    cookies: true,
    owasp: true
  });

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const response = await axios.get(`${API}/scans/advanced-web`);
      setScans(response.data);
    } catch (error) {
      console.error("Error fetching Advanced Web scans:", error);
    }
  };

  const startScan = async (e) => {
    e.preventDefault();
    if (!url) {
      alert("Por favor ingresa una URL");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/scans/advanced-web`, {
        url: url,
        options: options
      });
      
      setSelectedScan(response.data);
      setViewMode('results');
      fetchScans();
      setUrl('');
    } catch (error) {
      alert("Error ejecutando Advanced Web scan: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setViewMode('results');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-50';
    if (score >= 60) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setViewMode('form')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'form'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            🌐 Nuevo Scan Avanzado
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'history'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Historial ({scans.length})
          </button>
          {selectedScan && (
            <button
              onClick={() => setViewMode('results')}
              className={`px-4 py-2 font-medium ${
                viewMode === 'results'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📊 Resultados
            </button>
          )}
        </div>
      </div>

      {/* Form View */}
      {viewMode === 'form' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-2">🔐</span>
            Advanced Web Security Testing - OWASP WSTG
          </h2>

          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Pruebas avanzadas</strong> de seguridad web según OWASP:
              Headers HTTP, SSL/TLS, Cookies, Detección de tecnologías y más.
            </p>
          </div>

          <form onSubmit={startScan} className="space-y-6">
            <TargetSelect
              label="URL a escanear"
              value={url}
              onChange={setUrl}
              types={['domain', 'ip']}
              asUrl
              allowPort
              helpText="Solo objetivos con autorización vigente. Si la aplicación escucha en otro puerto, indicalo acá."
              testid="web-target-select"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Opciones de escaneo
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.headers}
                    onChange={(e) => setOptions({ ...options, headers: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">HTTP Headers</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.technologies}
                    onChange={(e) => setOptions({ ...options, technologies: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Tecnologías</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.ssl}
                    onChange={(e) => setOptions({ ...options, ssl: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">SSL/TLS</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.cookies}
                    onChange={(e) => setOptions({ ...options, cookies: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Cookies</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.owasp}
                    onChange={(e) => setOptions({ ...options, owasp: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">OWASP Config</span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !url}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {loading ? "🔄 Escaneando..." : "🚀 Iniciar Análisis Avanzado"}
            </button>
          </form>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Historial de Scans Avanzados
          </h3>

          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay scans avanzados registrados aún.</p>
              <button
                onClick={() => setViewMode('form')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Crear primer scan →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => (
                <div
                  key={scan.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 cursor-pointer transition"
                  onClick={() => viewScanDetails(scan)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{scan.url}</h4>
                      <p className="text-sm text-gray-500">
                        {new Date(scan.created_at).toLocaleString('es-AR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${getScoreColor(scan.results?.summary?.security_score || 0)}`}>
                        {scan.results?.summary?.security_score || 0}%
                      </div>
                      <p className="text-xs text-gray-500">Security Score</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && selectedScan && (
        <AdvancedWebResults scan={selectedScan} />
      )}
    </div>
  );
};

const AdvancedWebResults = ({ scan }) => {
  const [activeTab, setActiveTab] = useState('summary');

  const results = scan.results || {};
  const summary = results.summary || {};

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-50 border-green-200';
    if (score >= 60) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-100 text-red-800',
      'HIGH': 'bg-orange-100 text-orange-800',
      'MEDIUM': 'bg-yellow-100 text-yellow-800',
      'LOW': 'bg-blue-100 text-blue-800',
      'INFO': 'bg-gray-100 text-gray-800',
      'PASSED': 'bg-green-100 text-green-800'
    };
    return colors[severity] || colors['INFO'];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{scan.url}</h2>
            <p className="text-sm text-gray-500">
              Escaneado el {new Date(scan.created_at).toLocaleString('es-AR')}
            </p>
          </div>
          <div className={`text-center p-4 rounded-lg border-2 ${getScoreBg(summary.security_score || 0)}`}>
            <div className={`text-4xl font-bold ${getScoreColor(summary.security_score || 0)}`}>
              {summary.security_score || 0}%
            </div>
            <p className="text-xs text-gray-600 mt-1">Security Score</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">
              {results.http_headers?.success ? '✓' : '✗'}
            </div>
            <p className="text-sm text-gray-700 mt-1">HTTP Headers</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {results.technologies?.data?.web_servers?.length || 0}
            </div>
            <p className="text-sm text-gray-700 mt-1">Tecnologías</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">
              {results.ssl_tls?.data?.protocol_version || 'N/A'}
            </div>
            <p className="text-sm text-gray-700 mt-1">SSL/TLS</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-600">
              {results.cookies?.data?.total_cookies || 0}
            </div>
            <p className="text-sm text-gray-700 mt-1">Cookies</p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-indigo-600">
              {results.owasp_config?.data?.summary?.passed || 0}/{results.owasp_config?.data?.summary?.total || 0}
            </div>
            <p className="text-sm text-gray-700 mt-1">OWASP Checks</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 px-6">
            {['summary', 'headers', 'technologies', 'ssl', 'cookies', 'owasp'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium text-sm border-b-2 ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Resumen del Análisis</h3>
              
              <div className="grid gap-4">
                {/* OWASP Summary */}
                {results.owasp_config?.data && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">🛡️ Resumen OWASP</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Checks Pasados</p>
                        <p className="text-2xl font-bold text-green-600">
                          {results.owasp_config.data.summary.passed}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Checks Fallidos</p>
                        <p className="text-2xl font-bold text-red-600">
                          {results.owasp_config.data.summary.failed}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* SSL Summary */}
                {results.ssl_tls?.data && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-2">🔒 SSL/TLS</h4>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      <dt className="text-gray-600">Protocolo:</dt>
                      <dd className="font-medium">{results.ssl_tls.data.protocol_version}</dd>
                      <dt className="text-gray-600">Cipher:</dt>
                      <dd className="font-medium">{results.ssl_tls.data.cipher_suite?.name}</dd>
                      <dt className="text-gray-600">Seguro:</dt>
                      <dd className="font-medium">
                        {results.ssl_tls.data.is_secure_protocol ? '✓ Sí' : '✗ No'}
                      </dd>
                    </dl>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Headers Tab */}
          {activeTab === 'headers' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">HTTP Security Headers</h3>
              {results.http_headers?.data?.security_headers ? (
                <div className="space-y-3">
                  {Object.entries(results.http_headers.data.security_headers).map(([name, data]) => (
                    <div key={name} className={`border rounded-lg p-4 ${data.present ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{name}</h4>
                          {data.present ? (
                            <p className="text-sm text-gray-600 mt-1 font-mono">
                              {data.value}
                            </p>
                          ) : (
                            <p className="text-sm text-red-600 mt-1">
                              ⚠️ {data.recommendation}
                            </p>
                          )}
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${data.present ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {data.present ? '✓ Presente' : '✗ Faltante'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No hay datos de headers disponibles</p>
              )}
            </div>
          )}

          {/* Technologies Tab */}
          {activeTab === 'technologies' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Tecnologías Detectadas</h3>
              {results.technologies?.data ? (
                <div className="space-y-4">
                  {Object.entries(results.technologies.data).map(([category, items]) => (
                    <div key={category} className="border rounded-lg p-4">
                      <h4 className="font-medium text-blue-600 mb-2 capitalize">
                        {category.replace('_', ' ')}
                      </h4>
                      {Array.isArray(items) && items.length > 0 ? (
                        <ul className="space-y-1">
                          {items.map((item, idx) => (
                            <li key={idx} className="text-sm bg-gray-50 px-3 py-2 rounded">
                              {typeof item === 'object' ? `${item.name} (${item.version})` : item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No detectado</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No hay datos de tecnologías disponibles</p>
              )}
            </div>
          )}

          {/* SSL Tab */}
          {activeTab === 'ssl' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Análisis SSL/TLS</h3>
              {results.ssl_tls?.data ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded-lg p-4">
                      <label className="text-sm text-gray-600">Protocolo</label>
                      <p className="text-lg font-medium">{results.ssl_tls.data.protocol_version}</p>
                    </div>
                    <div className="border rounded-lg p-4">
                      <label className="text-sm text-gray-600">Cipher Suite</label>
                      <p className="text-lg font-medium">{results.ssl_tls.data.cipher_suite?.name}</p>
                    </div>
                    <div className="border rounded-lg p-4">
                      <label className="text-sm text-gray-600">Bits</label>
                      <p className="text-lg font-medium">{results.ssl_tls.data.cipher_suite?.bits}</p>
                    </div>
                    <div className="border rounded-lg p-4">
                      <label className="text-sm text-gray-600">Protocolo Seguro</label>
                      <p className="text-lg font-medium">
                        {results.ssl_tls.data.is_secure_protocol ? '✓ Sí' : '✗ No'}
                      </p>
                    </div>
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-2">Certificado</h4>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      <dt className="text-gray-600">Common Name:</dt>
                      <dd className="font-medium">{results.ssl_tls.data.certificate?.subject?.commonName}</dd>
                      <dt className="text-gray-600">Emisor:</dt>
                      <dd className="font-medium">{results.ssl_tls.data.certificate?.issuer?.organizationName}</dd>
                      <dt className="text-gray-600">Válido desde:</dt>
                      <dd className="font-medium">{results.ssl_tls.data.certificate?.not_before}</dd>
                      <dt className="text-gray-600">Válido hasta:</dt>
                      <dd className="font-medium">{results.ssl_tls.data.certificate?.not_after}</dd>
                    </dl>
                  </div>

                  {results.ssl_tls.data.recommendations?.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <h4 className="font-medium text-yellow-800 mb-2">⚠️ Recomendaciones</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {results.ssl_tls.data.recommendations.map((rec, idx) => (
                          <li key={idx} className="text-sm text-yellow-700">{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No hay datos SSL/TLS disponibles</p>
              )}
            </div>
          )}

          {/* Cookies Tab */}
          {activeTab === 'cookies' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Análisis de Cookies ({results.cookies?.data?.total_cookies || 0})
              </h3>
              {results.cookies?.data ? (
                <div>
                  {results.cookies.data.total_cookies > 0 ? (
                    <div className="space-y-3">
                      {results.cookies.data.cookies.map((cookie, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-gray-900">{cookie.name}</h4>
                              <p className="text-xs text-gray-500 mt-1">
                                Domain: {cookie.domain} | Path: {cookie.path}
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <span className={`px-2 py-1 text-xs rounded ${cookie.secure ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {cookie.secure ? '✓' : '✗'} Secure
                              </span>
                              <span className={`px-2 py-1 text-xs rounded ${cookie.httponly ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {cookie.httponly ? '✓' : '✗'} HttpOnly
                              </span>
                            </div>
                          </div>
                          {cookie.issues?.length > 0 && (
                            <ul className="mt-2 space-y-1">
                              {cookie.issues.map((issue, i) => (
                                <li key={i} className="text-sm text-red-600">⚠️ {issue}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}

                      {results.cookies.data.recommendations?.length > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                          <h4 className="font-medium text-blue-800 mb-2">💡 Recomendaciones</h4>
                          <ul className="list-disc list-inside space-y-1">
                            {results.cookies.data.recommendations.map((rec, idx) => (
                              <li key={idx} className="text-sm text-blue-700">{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No se detectaron cookies en este sitio</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">No hay datos de cookies disponibles</p>
              )}
            </div>
          )}

          {/* OWASP Tab */}
          {activeTab === 'owasp' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">Configuración OWASP</h3>
              {results.owasp_config?.data ? (
                <div className="space-y-4">
                  <div className={`border-2 rounded-lg p-4 ${getScoreBg(results.owasp_config.data.summary.compliance_score)}`}>
                    <div className="text-center">
                      <div className={`text-4xl font-bold ${getScoreColor(results.owasp_config.data.summary.compliance_score)}`}>
                        {results.owasp_config.data.summary.compliance_score}%
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {results.owasp_config.data.summary.passed}/{results.owasp_config.data.summary.total} checks pasados
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {Object.entries(results.owasp_config.data.checks).map(([name, check]) => (
                      <div key={name} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 capitalize">
                              {name.replace(/_/g, ' ')}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">{check.details}</p>
                          </div>
                          <span className={`px-3 py-1 text-xs font-medium rounded ${getSeverityBadge(check.severity)}`}>
                            {check.severity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No hay datos OWASP disponibles</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedWebScanner;
